import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { ReportEditingProvider } from '@/frontend/reports/primitives';
import { LoadedReportModule, evaluateReportRuntime } from '@/frontend/reports/loader';
import { ReportRenderBoundary } from '@/frontend/reports/ReportRenderBoundary';
import { CompiledReportBundle } from '@/reports/bundles/types';
import { PrintedTarget, locatePrinted, targetAt } from '@/reports/bundles/expectations';
import { InputsReportContext, ReportValues, RunReportContext } from '@/reports/types';
import { VerdictBadge } from '../runReports/ReportBadges';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

interface BundlePreviewProps {
  /** what the draft last compiled to; null when it does not compile */
  compiled: CompiledReportBundle | null;
  /** a run's, or for a report filled in from inputs, its sheet's (filled from the first fixture) */
  context: RunReportContext | InputsReportContext;
  /** the editor has changes the preview does not show yet */
  stale: boolean;
  /** what is asked on the report itself, as a fixture gives it; the defaults when absent */
  values?: ReportValues;
  /** picking a place in the document to check: drawn as it prints, and a click says where, and what it prints there */
  onPick?: (picked: { target: PrintedTarget; found: string[] }) => void;
}

/** what a click while picking points at, outlined as the pointer passes over it */
const PICKABLE = 'td, th, .entry, dl.kv, .header-meta > span, .tiles > *, h1, h2, h3, h4, p, li';

/** The width the report is laid out at; a narrower preview shows it scaled down, as a page, rather than squeezed. */
const PAGE_WIDTH = 680;

/**
 * The draft as last saved, running for real: the compiled scripts evaluated
 * exactly as the Report tab would, with editable values kept only here.
 */
export function BundlePreview({ compiled, context, stale, values: given, onPick }: BundlePreviewProps) {
  const loaded = useMemo<{ module: LoadedReportModule | null; error: string | null }>(() => {
    if (!compiled) return { module: null, error: null };
    try {
      return { module: evaluateReportRuntime({ bundleVersionId: `draft:${compiled.hash}`, key: compiled.key, contractVersion: compiled.contractVersion, definitionJs: compiled.definitionJs, documentJs: compiled.documentJs, hash: compiled.hash }), error: null };
    } catch (error) {
      return { module: null, error: error instanceof Error ? error.message : String(error) };
    }
  }, [compiled?.hash]);

  // the values are held with the module they were normalized for: new code never renders the old shape
  const [held, setHeld] = useState<{ module: LoadedReportModule; values: ReportValues } | null>(null);
  const values = held && held.module === loaded.module ? held.values : null;
  // New code: keep what was typed, normalized into the new shape. Other values given (another fixture): start from them, or the defaults.
  const givenKey = given === undefined ? null : JSON.stringify(given);
  const lastGiven = useRef(givenKey);
  useEffect(() => {
    const current = loaded.module;
    if (!current) return;
    const { definition } = current;
    const fresh = lastGiven.current !== givenKey;
    lastGiven.current = givenKey;
    setHeld((was) => ({ module: current, values: definition.normalizeValues(given ?? (fresh || !was ? definition.defaultValues() : was.values)) }));
  }, [loaded.module, givenKey]);
  const setValues = (next: ReportValues) => { if (loaded.module) setHeld({ module: loaded.module, values: next }); };

  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const outlined = useRef<HTMLElement | null>(null);
  const unmark = () => {
    if (outlined.current) outlined.current.style.outline = '';
    outlined.current = null;
  };
  useEffect(() => { if (!onPick) unmark(); }, [onPick]);
  const pickHandlers = onPick && frame ? {
    onMouseOver: (e: MouseEvent) => {
      unmark();
      const el = (e.target as Element).closest(PICKABLE) as HTMLElement | null;
      if (el && frame.contains(el)) {
        el.style.outline = '2px solid rgb(14 165 233)';
        outlined.current = el;
      }
    },
    onMouseLeave: unmark,
    onClickCapture: (e: MouseEvent) => {
      const target = targetAt(e.target as Element, frame);
      e.preventDefault();
      e.stopPropagation();
      if (target) {
        // what the clicked cell prints first: where a row or table repeats, the value checked is the one pointed at
        const found = locatePrinted(frame, target);
        const clicked = ((e.target as Element).closest('td, th')?.textContent ?? '').replace(/\s+/g, ' ').trim();
        const at = 'table' in target ? found.indexOf(clicked) : -1;
        onPick({ target, found: at > 0 ? [found[at], ...found.slice(0, at), ...found.slice(at + 1)] : found });
      }
    },
  } : {};
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (!frame || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      setZoom(width > 0 && width < PAGE_WIDTH ? width / PAGE_WIDTH : 1);
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frame]);

  const module = loaded.module;
  const model = useMemo(() => {
    if (!module || !values) return null;
    try {
      return { model: module.definition.evaluate(values, context as RunReportContext), error: null };
    } catch (error) {
      return { model: null, error: error instanceof Error ? error.message : String(error) };
    }
  }, [module, values, context]);

  if (!compiled) {
    return <PreviewMessage>{I18n.get('reportBundleNotCompiled')}</PreviewMessage>;
  }
  if (loaded.error || !module) {
    return <PreviewMessage error>{I18n.get('reportBundleLoadFailed', { message: loaded.error ?? '' })}</PreviewMessage>;
  }
  if (!values || !model) return null;
  if (model.error || !model.model) {
    return <PreviewMessage error>{I18n.get('reportBundleLoadFailed', { message: model.error ?? '' })}</PreviewMessage>;
  }

  const { Document } = module.components;
  // the components take whichever context their subject reads; the registry holds them erased to the run's
  const viewContext = context as RunReportContext;
  const meta = { reportVersion: module.definition.version };
  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-background/95 px-3 py-1.5 backdrop-blur">
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <span className="truncate font-medium">{module.definition.title}</span>
          <span className="shrink-0 text-muted-foreground">v{module.definition.version}</span>
          <VerdictBadge verdict={model.model.verdict} label={model.model.verdictLabel} />
          {stale && <Badge variant="outline" className="shrink-0 border-amber-300 bg-amber-50 text-amber-800">{I18n.get('reportBundlePreviewStale')}</Badge>}
        </div>
        <Button variant="ghost" size="xs" className="shrink-0 text-muted-foreground" onClick={() => setValues(module.definition.defaultValues())}><RotateCcw /> {I18n.get('reportBundleResetValues')}</Button>
      </div>
      {/* from here down is the report itself, styled as on a run's Report tab; in a narrow preview, scaled to fit */}
      <div ref={setFrame} {...pickHandlers} className={onPick ? 'cursor-crosshair' : undefined}>
        <div data-slot="report-preview" className="d-flex flex-column gap-3 pt-3" style={zoom < 1 ? { zoom, width: PAGE_WIDTH } : undefined}>
          <ReportRenderBoundary resetKey={compiled.hash} fallback={(message) => <PreviewMessage error>{I18n.get('reportBundleLoadFailed', { message })}</PreviewMessage>}>
            <ReportEditingProvider readOnly={!!onPick}>
              <Document values={values} model={model.model} context={viewContext} meta={meta} onChange={setValues} />
            </ReportEditingProvider>
          </ReportRenderBoundary>
        </div>
      </div>
    </div>
  );
}

function PreviewMessage({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <p className={error ? 'p-3 text-xs text-destructive' : 'p-3 text-xs text-muted-foreground'}>{children}</p>;
}
